import * as React from 'react';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';

import type {FilterObject} from 'nutshell-core/types';
import {isFirstLoad} from 'nutshell-core/utils/graphql-loading';

import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {MessagePerformanceListItem} from 'shells/drip-sequence-performance/message-performance-list-item';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error - imported from .js/.jsx file
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EnvelopeIcon} from 'shells/icon/svg/envelope-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReportsIcon} from 'shells/icon/svg/reports-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {
    DripSequenceFragment as DripSequence,
    DripSequenceStubFragment as DripSequenceStub,
    DripSequenceStartTriggerSet as DripSequenceStartTriggerSetMutation,
    DripSequenceStartTriggerSetVariables as DripSequenceStartTriggerSetMutationVariables,
    DripSequenceGoalSet as DripSequenceGoalSetMutation,
    DripSequenceGoalSetVariables as DripSequenceGoalSetMutationVariables,
    EditionFragment as DripEdition,
    CampaignUpdateInput,
    EditionUpdateInput,
    StartTriggerSetInput,
    DripSequenceGoalSetInput,
} from 'nutshell-graphql-types';

import {PageContainer, PageTitleBar} from '../../../layout';
import type {ReportURLParams} from '../../../reports/report-types';

import {appHistory} from '../../../../history';

import {EngagementBar} from '../../engagement/engagement-bar';
import {DripSequenceMutations} from '../../graphql-wrappers/drip-sequence/drip-sequence-mutations';
import {DripSequenceQuery} from '../../graphql-wrappers/drip-sequence/drip-sequence-query';
import {DripSequenceEditionMutations} from '../../graphql-wrappers/drip-sequence-edition/drip-sequence-edition-mutations';
import {DripSequenceEditionsQuery} from '../../graphql-wrappers/drip-sequence-edition/drip-sequence-editions-query';

import {DRIP_RECIPIENT_STATUS_FILTERS} from '../consts';
import {DripSequenceEngagement} from '../engagement/drip-sequence-engagement';
import DRIP_SEQUENCE_GOAL_SET from '../graphql/mutations/drip-sequence-goal-set.graphql';
import DRIP_SEQUENCE_START_TRIGGER_SET from '../graphql/mutations/drip-sequence-start-trigger-set.graphql';
import {dripSequenceFieldFilterFunction, getDripSequenceStatusBarInfo} from '../helpers';
import {DripSequenceRecipientList} from '../recipient-list/drip-sequence-recipient-list';

import {ControlButtons} from './control-buttons';
import {DripSequenceMessage} from './drip-sequence-message';

import styles from './drip-sequence-page.module.css';

export type ModalComponent = React.ComponentType<{
    isOpen: boolean;
    onClose: () => void;
}>;

type OwnProps = ContextRouter & {
    isArchived?: boolean;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    isArchived: boolean;
    isLoading: boolean;
    dripSequence: DripSequence | null | undefined;
    dripSequenceStub: DripSequenceStub | null | undefined;
    campaignDetailsError: string | null | undefined;
    editionsListError: string | null | undefined;
    editions: DripEdition[];
    onCreateEdition: (name: string, campaignId: string) => Promise<any>;
    onDuplicateEdition: (
        editionId: string,
        campaignId: string,
        name?: string | null | undefined
    ) => Promise<any>;
    onRemoveEdition: (editionId: string, campaignId: string) => Promise<any>;
    onDripSequenceUpdate: (input: CampaignUpdateInput) => Promise<any>;
    onEditionUpdate: (input: EditionUpdateInput) => Promise<any>;
    refetchDripSequence: () => Promise<any>;
    onSetStartTrigger: (input: StartTriggerSetInput) => Promise<any>;
    onSetGoal: (input: DripSequenceGoalSetInput) => Promise<any>;
    refetchDripEditions: () => Promise<any>;
};

function DripSequencePageComponent(props: Props) {
    const activeTab: string = props.match.params.tab || 'messages';

    const dripSequenceData = props.dripSequence || props.dripSequenceStub;
    const isDraft = dripSequenceData && dripSequenceData.status === 'DRAFT';
    const title = dripSequenceData ? dripSequenceData.name : 'Drip sequence';

    const tabOptions = [
        {
            id: 'messages',
            label: 'Messages',
            icon: EnvelopeIcon,
            href: generatePath(props.match.path, {
                dripSequenceId: props.match.params.dripSequenceId,
                tab: 'messages',
            }),
            component: (
                <DripSequenceMessage
                    dripSequence={props.dripSequence}
                    editions={props.editions}
                    isLoading={props.isLoading}
                    onRemoveEdition={props.onRemoveEdition}
                    onDripSequenceUpdate={props.onDripSequenceUpdate}
                    onEditionUpdate={props.onEditionUpdate}
                    refetchDripSequence={props.refetchDripSequence}
                    refetchDripEditions={props.refetchDripEditions}
                    onSetStartTrigger={props.onSetStartTrigger}
                    onSetGoal={props.onSetGoal}
                    isDraft={isDraft || false}
                    onCreateEdition={props.onCreateEdition}
                    onDuplicateEdition={props.onDuplicateEdition}
                />
            ),
        },
        {
            id: 'engagement',
            label: 'Engagement',
            icon: ReportsIcon,
            href: generatePath(props.match.path, {
                dripSequenceId: props.match.params.dripSequenceId,
                tab: 'engagement',
            }),
            component: (
                <div className='flex full-height'>
                    {props.isLoading || !props.dripSequence ? (
                        <LoadingPage />
                    ) : (
                        <DripSequenceEngagement
                            dripSequence={props.dripSequence}
                            editions={props.editions}
                            engagementBar={
                                <EngagementBar
                                    engagementStats={props.dripSequence.engagementStats}
                                    isLoading={false}
                                    label='Engagement'
                                />
                            }
                            renderRow={(edition: DripEdition) => {
                                const isDraftEdition = edition.status === 'NEW';

                                return (
                                    <MessagePerformanceListItem
                                        name={edition.name}
                                        thumbnailUrl={
                                            edition.emailTemplate &&
                                            edition.emailTemplate.thumbnailUrl
                                                ? edition.emailTemplate.thumbnailUrl
                                                : ''
                                        }
                                        htmlUrlPath={edition.htmlUrlPath}
                                        isDraft={isDraftEdition}
                                        metricStats={{
                                            totalSentCount: edition.engagementStats.totalSentCount,
                                            openRate: edition.engagementStats.openRate,
                                            clickRate: edition.engagementStats.clickRate,
                                        }}
                                        metricKeys={['totalSentCount', 'openRate', 'clickRate']}
                                        statItems={[
                                            {
                                                stat: {
                                                    rate: edition.engagementStats
                                                        .dripProgressionsGoalMetRate,
                                                    count: edition.engagementStats
                                                        .dripProgressionsGoalMet,
                                                    linkTo: '#',
                                                },
                                                nounSingular: 'Goal met',
                                                nounPlural: 'Goals met',
                                                isLoading: false,
                                            },
                                            {
                                                stat: {
                                                    rate: edition.engagementStats.unsubscribeRate,
                                                    count: edition.engagementStats.unsubscribeCount,
                                                    linkTo: '#',
                                                },
                                                nounSingular: 'Unsubscribe',
                                                nounPlural: 'Unsubscribes',
                                                isLoading: false,
                                            },
                                            {
                                                stat: {
                                                    rate: edition.engagementStats
                                                        .permanentBounceRate,
                                                    count: edition.engagementStats
                                                        .permanentBounceCount,
                                                    linkTo: '#',
                                                },
                                                nounSingular: 'Bounce',
                                                nounPlural: 'Bounces',
                                                isLoading: false,
                                            },
                                        ]}
                                    />
                                );
                            }}
                        />
                    )}
                </div>
            ),
        },
    ];

    if (!isDraft) {
        tabOptions.push({
            id: 'recipients',
            label: 'Recipients',
            icon: AudienceIcon,
            href: generatePath(props.match.path, {
                dripSequenceId: props.match.params.dripSequenceId,
                tab: 'recipients',
            }),
            component: (
                <DripSequenceRecipientList
                    dripSequence={props.dripSequence}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    // @ts-expect-error
                    statusFilters={DRIP_RECIPIENT_STATUS_FILTERS}
                    fieldFilterFunction={dripSequenceFieldFilterFunction}
                />
            ),
        });
    }

    return (
        <PageContainer
            title={title}
            pageTitleBar={
                <PageTitleBar
                    editInPlaceProps={
                        dripSequenceData
                            ? {
                                  onChange: (newValue: string | null | undefined) => {
                                      return props.onDripSequenceUpdate({
                                          id: dripSequenceData.id,
                                          name: newValue,
                                      });
                                  },
                                  placeholder: 'Name this drip sequence',
                              }
                            : undefined
                    }
                    title={title}
                    subheaderPrimaryText='Edit your drip sequence'
                    noMaxWidth={true}
                    breadcrumbs={[
                        {
                            to: props.isArchived
                                ? '/email-marketing/drip-sequences/archived'
                                : '/email-marketing/drip-sequences',
                            text: 'Drip sequences',
                        },
                    ]}
                    buttons={
                        <ControlButtons
                            isLoading={props.isLoading}
                            editions={props.editions}
                            dripSequence={props.dripSequence}
                            refetchDripSequence={props.refetchDripSequence}
                            refetchDripEditions={props.refetchDripEditions}
                        />
                    }
                />
            }
            controlBar={
                <PageControlBar
                    statusInfo={
                        props.dripSequence
                            ? getDripSequenceStatusBarInfo(props.dripSequence)
                            : undefined
                    }
                    tabs={{
                        selectedTab: activeTab,
                        options: tabOptions,
                    }}
                />
            }
        >
            <div className={styles['drip-sequence-container']}>
                {props.campaignDetailsError ? (
                    <ErrorState errorMessage={props.campaignDetailsError} />
                ) : undefined}
                {props.editionsListError ? (
                    <ErrorState errorMessage={props.editionsListError} />
                ) : undefined}
                {/* @ts-expect-error */}
                <TabbedContent selectedTab={activeTab} tabOptions={tabOptions} />
            </div>
        </PageContainer>
    );
}

export function DripSequencePage(props: OwnProps) {
    // We won't be here without this param, but flow really needs
    // it to have a default value so here we go.
    const dripSequenceIdParam = props.match.params.dripSequenceId;
    const dripSequenceId = dripSequenceIdParam ? dripSequenceIdParam : '';

    const [setStartTrigger] = useMutation<
        DripSequenceStartTriggerSetMutation,
        DripSequenceStartTriggerSetMutationVariables
    >(DRIP_SEQUENCE_START_TRIGGER_SET);

    const [setGoal] = useMutation<
        DripSequenceGoalSetMutation,
        DripSequenceGoalSetMutationVariables
    >(DRIP_SEQUENCE_GOAL_SET);

    return (
        <DripSequenceQuery dripSequenceId={dripSequenceId}>
            {({dripSequence, dripSequenceStub, error: campaignDetailsError, refetch}) => (
                <DripSequenceEditionsQuery dripSequenceId={dripSequenceId} isArchived={false}>
                    {({editions, error, refetch: refetchDripEditions, networkStatus}) => (
                        <DripSequenceEditionMutations>
                            {({onCreate, onDuplicate, onUpdate: onUpdateEdition, onDelete}) => (
                                <DripSequenceMutations>
                                    {({onUpdate: onUpdateDripSequence}) => (
                                        <DripSequencePageComponent
                                            {...props}
                                            dripSequence={dripSequence}
                                            dripSequenceStub={dripSequenceStub}
                                            // Only need to disable on the first load?
                                            isLoading={isFirstLoad(networkStatus)}
                                            campaignDetailsError={campaignDetailsError}
                                            editionsListError={error}
                                            editions={editions}
                                            onSetStartTrigger={(input: StartTriggerSetInput) => {
                                                return setStartTrigger({
                                                    variables: {input},
                                                }).then(() => {
                                                    refetch();
                                                });
                                            }}
                                            onSetGoal={(input: DripSequenceGoalSetInput) => {
                                                return setGoal({
                                                    variables: {input},
                                                }).then(() => {
                                                    refetch();
                                                });
                                            }}
                                            // @ts-expect-error
                                            onCreateEdition={(name, campaignId) =>
                                                onCreate(name, campaignId).then((newEdition) => {
                                                    if (newEdition)
                                                        appHistory.push(newEdition.htmlUrlPath);
                                                })
                                            }
                                            // @ts-expect-error
                                            onDuplicateEdition={(editionId, campaignId, name) =>
                                                onDuplicate(editionId, campaignId, name).then(
                                                    (newEdition) => {
                                                        if (newEdition)
                                                            appHistory.push(newEdition.htmlUrlPath);
                                                    }
                                                )
                                            }
                                            onDripSequenceUpdate={(input: CampaignUpdateInput) =>
                                                onUpdateDripSequence(input)
                                            }
                                            onEditionUpdate={(input: EditionUpdateInput) =>
                                                onUpdateEdition(input)
                                            }
                                            // @ts-expect-error
                                            onRemoveEdition={(editionId, campaignId) =>
                                                onDelete(editionId, campaignId).then(() => {
                                                    refetchDripEditions();
                                                })
                                            }
                                            refetchDripSequence={refetch}
                                            refetchDripEditions={refetchDripEditions}
                                            activeFilters={props.activeFilters}
                                            onParamChange={props.onParamChange}
                                            urlParams={props.urlParams}
                                            onRemoveAllFilters={props.onRemoveAllFilters}
                                        />
                                    )}
                                </DripSequenceMutations>
                            )}
                        </DripSequenceEditionMutations>
                    )}
                </DripSequenceEditionsQuery>
            )}
        </DripSequenceQuery>
    );
}
